import { useEffect, useMemo, useRef, useState } from "react";
import { Upload, Search, Send, RefreshCw, Loader2, FileSpreadsheet, X } from "lucide-react";
import client from "../../lib/api";
import ConfirmDialog from "../../components/ConfirmDialog";
import { PageHeader, StatusPill, PrivacyBadge, STATUS_META } from "./adminUi";

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const first = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const hasHeader = first.includes("email") || first.includes("name");
  const cols = hasHeader ? first : ["name", "email", "program", "term"];
  const start = hasHeader ? 1 : 0;
  const at = (parts, key, fallbackIdx) => {
    const i = cols.indexOf(key);
    return (i >= 0 ? parts[i] : parts[fallbackIdx]) || "";
  };
  const rows = [];
  for (let i = start; i < lines.length; i++) {
    const parts = lines[i].split(",").map((p) => p.trim());
    const email = at(parts, "email", 1).toLowerCase();
    if (!email || !email.includes("@")) continue;
    rows.push({ name: at(parts, "name", 0), email, program: at(parts, "program", 2), term: at(parts, "term", 3) });
  }
  return rows;
}

const STATUS_FILTERS = ["all", "invited", "signed_up", "in_progress", "completed"];

export default function AdminRosterScreen() {
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [resending, setResending] = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [parsed, setParsed] = useState([]);
  const [fileName, setFileName] = useState("");
  const [notify, setNotify] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/roster");
      setStudents(data.students || []);
      setSummary(data.summary || null);
      setEmailConfigured(!!data.email_configured);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return students.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (!needle) return true;
      return (s.name || "").toLowerCase().includes(needle) || (s.email || "").toLowerCase().includes(needle) || (s.program || "").toLowerCase().includes(needle);
    });
  }, [students, q, statusFilter]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const text = await f.text();
    setParsed(parseCsv(text));
  };

  const doUpload = async () => {
    const { data } = await client.post("/admin/roster/upload", { rows: parsed, notify });
    setConfirmOpen(false);
    setUploadOpen(false);
    setParsed([]); setFileName("");
    const em = data.emails || {};
    setToast(`Imported ${data.created} students. ${notify ? `${em.sent} emailed, ${em.queued} queued.` : ""}`.trim());
    await load();
    setTimeout(() => setToast(""), 6000);
  };

  const resend = async (id) => {
    setResending(id);
    try { await client.post(`/admin/roster/${id}/resend`); setToast("Invite re-sent."); setTimeout(() => setToast(""), 4000); }
    finally { setResending(null); }
  };

  return (
    <div data-testid="admin-roster">
      <PageHeader
        title="Students"
        subtitle="Everyone your college has invited, and where each one is. Status only, never a student's read."
        actions={
          <>
            <PrivacyBadge />
            <button className="k-btn k-btn-primary !py-2" onClick={() => setUploadOpen(true)} data-testid="roster-import-btn">
              <Upload size={15} /> Import students
            </button>
          </>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {["invited", "signed_up", "in_progress", "completed"].map((k) => (
            <button
              key={k}
              onClick={() => setStatusFilter(statusFilter === k ? "all" : k)}
              className="k-card text-left"
              style={{ padding: "0.9rem 1.1rem", outline: statusFilter === k ? `2px solid ${STATUS_META[k].color}` : "none" }}
              data-testid={`roster-stat-${k}`}
            >
              <div className="text-xs" style={{ color: STATUS_META[k].color, fontWeight: 600 }}>{STATUS_META[k].label}</div>
              <div className="klareet-mono" style={{ fontSize: "1.5rem", color: "#131822", lineHeight: 1.1 }}>{summary[k]}</div>
            </button>
          ))}
        </div>
      )}

      <div className="k-card" style={{ padding: 0 }} data-testid="roster-table-card">
        <div className="flex flex-wrap items-center gap-3 p-4" style={{ borderBottom: "1px solid rgba(19,24,34,0.08)" }}>
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "rgba(19,24,34,0.4)" }} />
            <input className="k-input !pl-9" placeholder="Search name, email, program" value={q} onChange={(e) => setQ(e.target.value)} data-testid="roster-search" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className="px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ background: statusFilter === s ? "#131822" : "rgba(19,24,34,0.05)", color: statusFilter === s ? "white" : "rgba(19,24,34,0.7)" }}
                data-testid={`roster-filter-${s}`}
              >
                {s === "all" ? "All" : STATUS_META[s].label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" size={20} /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left" data-testid="roster-table">
              <thead>
                <tr style={{ color: "rgba(19,24,34,0.6)" }}>
                  {["Student", "Program", "Term", "Status", ""].map((h, i) => (
                    <th key={i} className="text-xs uppercase tracking-wider font-semibold px-4 py-3" style={{ borderBottom: "1px solid rgba(19,24,34,0.08)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-[rgba(19,24,34,0.02)]" data-testid={`roster-row-${s.id}`}>
                    <td className="px-4 py-3" style={{ borderBottom: "1px solid rgba(19,24,34,0.05)" }}>
                      <div style={{ color: "#131822", fontWeight: 500 }}>{s.name}</div>
                      <div className="klareet-mono text-xs" style={{ color: "rgba(19,24,34,0.55)" }}>{s.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ borderBottom: "1px solid rgba(19,24,34,0.05)", color: "rgba(19,24,34,0.75)" }}>{s.program || "-"}</td>
                    <td className="px-4 py-3 klareet-mono text-xs" style={{ borderBottom: "1px solid rgba(19,24,34,0.05)", color: "rgba(19,24,34,0.65)" }}>{s.term || "-"}</td>
                    <td className="px-4 py-3" style={{ borderBottom: "1px solid rgba(19,24,34,0.05)" }}><StatusPill status={s.status} /></td>
                    <td className="px-4 py-3 text-right" style={{ borderBottom: "1px solid rgba(19,24,34,0.05)" }}>
                      {s.status === "invited" && (
                        <button className="k-btn k-btn-ghost !py-1 !px-2 !text-xs" onClick={() => resend(s.id)} disabled={resending === s.id} data-testid={`roster-resend-${s.id}`}>
                          {resending === s.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Resend
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-16 text-center k-quiet" data-testid="roster-empty">No students match this view.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-3 k-quiet !text-xs" data-testid="roster-count">Showing {filtered.length} of {students.length} students</div>
      </div>

      {toast && <div className="fixed bottom-6 right-6 k-card px-4 py-3 text-sm z-40" style={{ background: "#131822", color: "white" }} data-testid="roster-toast">{toast}</div>}

      {/* Import modal */}
      {uploadOpen && (
        <div className="k-session-overlay" role="dialog" aria-modal="true" data-testid="roster-upload-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setUploadOpen(false); }}>
          <div className="k-session-card k-fade-in" style={{ maxWidth: "34rem" }}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="klareet-serif" style={{ fontSize: "1.4rem", color: "#131822" }}>Import students</h2>
                <p className="k-quiet mt-1 !text-sm">A CSV with columns name, email, program, term. Email is the only required column.</p>
              </div>
              <button onClick={() => setUploadOpen(false)} className="k-btn k-btn-ghost !p-1.5" data-testid="roster-upload-close"><X size={16} /></button>
            </div>

            <div
              className="mt-4 rounded-xl p-6 text-center cursor-pointer"
              style={{ border: "1.5px dashed rgba(19,24,34,0.2)", background: "rgba(19,24,34,0.02)" }}
              onClick={() => fileRef.current?.click()}
              data-testid="roster-dropzone"
            >
              <FileSpreadsheet size={26} className="mx-auto mb-2" style={{ color: "#1D5FD4" }} />
              <div className="text-sm font-medium" style={{ color: "#131822" }}>{fileName || "Choose a CSV file"}</div>
              <div className="k-quiet !text-xs mt-1">{parsed.length ? `${parsed.length} valid rows found` : "click to browse"}</div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="roster-file-input" />
            </div>

            {parsed.length > 0 && (
              <div className="mt-3 rounded-lg overflow-hidden" style={{ border: "1px solid rgba(19,24,34,0.08)" }}>
                <table className="w-full text-xs">
                  <tbody>
                    {parsed.slice(0, 4).map((r, i) => (
                      <tr key={i}><td className="px-3 py-1.5" style={{ borderBottom: "1px solid rgba(19,24,34,0.05)" }}>{r.name || "-"}</td><td className="px-3 py-1.5 klareet-mono" style={{ borderBottom: "1px solid rgba(19,24,34,0.05)", color: "rgba(19,24,34,0.6)" }}>{r.email}</td></tr>
                    ))}
                  </tbody>
                </table>
                {parsed.length > 4 && <div className="px-3 py-1.5 k-quiet !text-xs">and {parsed.length - 4} more</div>}
              </div>
            )}

            <label className="flex items-center gap-2.5 mt-4 text-sm cursor-pointer" data-testid="roster-notify-toggle">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
              <span style={{ color: "#131822" }}>Email an invite to each student</span>
            </label>
            {!emailConfigured && notify && <div className="k-quiet !text-xs mt-1">Sending is not configured yet, so invites will be queued and logged.</div>}

            <div className="mt-5 flex justify-end gap-2">
              <button className="k-btn k-btn-ghost" onClick={() => setUploadOpen(false)} data-testid="roster-upload-cancel">Cancel</button>
              <button className="k-btn k-btn-primary" disabled={!parsed.length} onClick={() => setConfirmOpen(true)} data-testid="roster-upload-continue">
                <Send size={15} /> Import {parsed.length || ""} students
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doUpload}
        title={notify ? `Import and invite ${parsed.length} students?` : `Import ${parsed.length} students?`}
        description={notify ? "Each new student gets one invitation email to start with Klair. Their answers stay private to them." : "Students are added to your roster. No email is sent."}
        consequences={notify ? [`Up to ${parsed.length} invitation emails will be prepared`, "Existing students are updated, not duplicated"] : ["Existing students are updated, not duplicated"]}
        confirmWord="SEND"
        confirmLabel={notify ? "Import and invite" : "Import"}
        tone="warning"
        testId="roster-confirm"
      />
    </div>
  );
}
